import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, QrCode, MessageCircle, Repeat, TrendingUp,
  Gift, Share2, Sparkles, ArrowRight, ShoppingBag, Loader2
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { dashboard } from "@/lib/api";
import { formatIDR, formatNumber } from "@/lib/format";

const KpiCard = ({ icon: Icon, label, value, sub, color = "emerald", testid }) => (
  <Card className="border-gray-200 hover:border-emerald-200 hover:-translate-y-0.5 transition-all" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg bg-${color}-50 flex items-center justify-center text-${color}-600`}>
          <Icon size={18} strokeWidth={2} />
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs font-semibold text-gray-500">{label}</div>
        <div className="mt-1 font-heading text-2xl font-bold text-gray-900">{value}</div>
        {sub && <div className="mt-1 text-xs text-gray-500">{sub}</div>}
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [rec, setRec] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([dashboard.stats(), dashboard.dailyRec(), dashboard.revenueTrend()])
      .then(([s, r, t]) => {
        setStats(s);
        setRec(r);
        setTrend(t);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Ringkasan performa toko Anda hari ini.</p>
      </div>

      {/* North star + AI recommendation */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
          <div className="relative">
            <div className="text-xs font-bold tracking-widest uppercase text-emerald-100">North Star Metric</div>
            <div className="mt-2 flex items-end gap-3">
              <div className="font-heading text-5xl sm:text-6xl font-bold">{stats?.repeat_order_rate || 0}%</div>
              <div className="pb-2 text-emerald-100 text-sm font-semibold">Repeat Order Rate</div>
            </div>
            <div className="mt-4 flex items-center gap-6 text-sm">
              <div>
                <div className="text-emerald-100 text-xs">Total Order</div>
                <div className="font-semibold text-lg">{formatNumber(stats?.total_orders || 0)}</div>
              </div>
              <div>
                <div className="text-emerald-100 text-xs">Repeat Order</div>
                <div className="font-semibold text-lg">{formatNumber(stats?.total_repeat_orders || 0)}</div>
              </div>
              <div>
                <div className="text-emerald-100 text-xs">Revenue Campaign</div>
                <div className="font-semibold text-lg">{formatIDR(stats?.revenue_from_campaign || 0)}</div>
              </div>
            </div>
          </div>
        </div>

        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-amber-700">
              <Sparkles size={14} /> Rekomendasi AI
            </div>
            <div className="mt-3 font-heading text-lg font-semibold text-gray-900 leading-snug">{rec?.title || "-"}</div>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">{rec?.body || ""}</p>
            {rec?.estimated_impact && (
              <div className="mt-3 inline-block text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                {rec.estimated_impact}
              </div>
            )}
            {rec?.action_url && (
              <Link to={rec.action_url}>
                <Button className="mt-4 w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="ai-rec-cta">
                  {rec.action} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard icon={Users} label="Total Customer" value={formatNumber(stats?.total_customers || 0)} color="emerald" testid="kpi-customers" />
        <KpiCard icon={QrCode} label="Total QR Scan" value={formatNumber(stats?.total_qr_scans || 0)} color="blue" testid="kpi-scans" />
        <KpiCard icon={MessageCircle} label="WhatsApp Opt-in" value={formatNumber(stats?.total_whatsapp_optin || 0)} color="green" testid="kpi-optin" />
        <KpiCard icon={Repeat} label="Member Aktif" value={formatNumber(stats?.active_members || 0)} color="purple" testid="kpi-members" />
        <KpiCard icon={Gift} label="Cashback Terpakai" value={formatIDR(stats?.cashback_used || 0)} color="amber" testid="kpi-cashback" />
        <KpiCard icon={Share2} label="Referral" value={formatNumber(stats?.referral_sales || 0)} color="pink" testid="kpi-referral" />
      </div>

      {/* Chart + Top Products */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-semibold text-gray-900">Tren Revenue</h3>
                <p className="text-xs text-gray-500 mt-0.5">Revenue & repeat order per bulan</p>
              </div>
            </div>
            <div className="mt-4 h-64">
              {trend.length > 0 ? (
                <ResponsiveContainer>
                  <LineChart data={trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => formatIDR(v)} />
                    <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <TrendingUp className="text-gray-300" size={48} />
                  <p className="mt-3 text-sm text-gray-500">Belum ada data order.</p>
                  <p className="text-xs text-gray-400">Tambahkan order pertama untuk melihat tren.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200">
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold text-gray-900">Top Repeat Products</h3>
            <p className="text-xs text-gray-500 mt-0.5">Produk paling sering dibeli ulang</p>
            <div className="mt-4 space-y-3">
              {stats?.top_repeat_products?.length > 0 ? stats.top_repeat_products.map((p, i) => (
                <div key={p.product || `top-${i}`} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-700 text-xs font-bold">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">{p.product}</div>
                    <div className="text-xs text-gray-500">{p.count}x repeat</div>
                  </div>
                  <div className="text-sm font-semibold text-emerald-600">{formatIDR(p.revenue)}</div>
                </div>
              )) : (
                <div className="text-center py-8">
                  <ShoppingBag className="text-gray-300 mx-auto" size={40} />
                  <p className="mt-2 text-sm text-gray-500">Belum ada repeat order</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <Card className="border-gray-200">
        <CardContent className="p-6">
          <h3 className="font-heading font-semibold text-gray-900">Aksi Cepat</h3>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Link to="/dashboard/qr-loyalty">
              <Button variant="outline" className="w-full h-auto py-3 flex-col items-start gap-1" data-testid="quick-qr">
                <QrCode className="text-emerald-600" size={18} />
                <span className="font-semibold text-sm">Buat QR Loyalty</span>
              </Button>
            </Link>
            <Link to="/dashboard/campaigns">
              <Button variant="outline" className="w-full h-auto py-3 flex-col items-start gap-1" data-testid="quick-campaign">
                <MessageCircle className="text-emerald-600" size={18} />
                <span className="font-semibold text-sm">Kirim Campaign</span>
              </Button>
            </Link>
            <Link to="/dashboard/customers">
              <Button variant="outline" className="w-full h-auto py-3 flex-col items-start gap-1" data-testid="quick-customers">
                <Users className="text-emerald-600" size={18} />
                <span className="font-semibold text-sm">Lihat Customer</span>
              </Button>
            </Link>
            <Link to="/dashboard/ai">
              <Button variant="outline" className="w-full h-auto py-3 flex-col items-start gap-1" data-testid="quick-ai">
                <Sparkles className="text-amber-600" size={18} />
                <span className="font-semibold text-sm">AI Assistant</span>
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
